import { IdTokenClaims } from "@/types/auth.types";
import { DynamicsContact } from "@/types/dynamics.types";

const normalize = (value?: string | null): string =>
  (value ?? "").trim().toLowerCase();

const isUnknownPlaceholder = (value: string): boolean =>
  normalize(value) === "unknown";

/**
 * Company / business identifier from the identity provider only (never inferred from CRM orgs).
 * Matches backend CurrentUserService: department
 */
export function getIdentityCompanyIdFromClaims(
  claims?: IdTokenClaims | null,
): string | undefined {
  if (!claims) {
    return undefined;
  }

  const deptClaim = claims.department;
  const dept = typeof deptClaim === "string" ? deptClaim.trim() : "";
  if (dept.length > 0 && !isUnknownPlaceholder(dept)) {
    return dept;
  }

  return undefined;
}

export const isBusinessRole = (jobTitle?: string): boolean =>
  jobTitle === "BUSINESSADMIN" || jobTitle === "BUSINESSUSER";

/**
 * Resolves CRM identity.userType to a pseudo JobTitle so company gating stays consistent
 * when MyID JobTitle is stale versus Dynamics (e.g. still CITIZEN while identity is business).
 */
function getEffectiveBusinessJobTitle(
  jobTitle?: string,
  identityUserType?: number | string | null,
): string | undefined {
  if (isBusinessRole(jobTitle)) {
    return jobTitle;
  }

  const ut =
    identityUserType === undefined || identityUserType === null
      ? NaN
      : Number(identityUserType);
  if (ut === 1) {
    return "BUSINESSADMIN";
  }
  if (ut === 2 || ut === 4) {
    return "BUSINESSUSER";
  }

  return jobTitle;
}

export const hasOwnBusinessOrganization = (
  contactData?: DynamicsContact,
  businessId?: string | null,
): boolean => {
  const normalizedBusinessId = normalize(businessId);
  if (!normalizedBusinessId) {
    return false;
  }

  const organizations = contactData?.organizations ?? [];

  return organizations.some((organization) => {
    const accountBusinessId = normalize(organization?.account?.businessId);
    const contactBusinessId = normalize(
      // Some payloads surface businessId on the AccountContact-level DTO
      // via organization.businessId in addition to organization.account.businessId.
      (organization as any)?.businessId ?? undefined,
    );

    return (
      accountBusinessId === normalizedBusinessId ||
      contactBusinessId === normalizedBusinessId
    );
  });
};

export const requiresCompanySetup = (params: {
  jobTitle?: string;
  businessId?: string | null;
  contactData?: DynamicsContact;
}): boolean => {
  const { jobTitle, businessId, contactData } = params;

  const hasClaimedCompanyId = Boolean((businessId ?? "").trim());
  if (!hasClaimedCompanyId) {
    return false;
  }

  if (hasOwnBusinessOrganization(contactData, businessId)) {
    return false;
  }

  const effectiveJobTitle = getEffectiveBusinessJobTitle(
    jobTitle,
    contactData?.identity?.userType,
  );
  const isBusinessByTokenOrCrm = isBusinessRole(effectiveJobTitle);

  // Personal / invited users can still have CRM organization rows that do not match the
  // identity provider company id (department claim).
  const hasCrmOrganizationRows = (contactData?.organizations?.length ?? 0) > 0;

  return isBusinessByTokenOrCrm || hasCrmOrganizationRows;
};
